import React, { useState } from "react";
import { useAIStream } from "../hooks/useAIStream";
import { StreamErrorBoundary as AIStreamErrorBoundary } from "./StreamErrorBoundary";
import {
  Sparkles,
  Zap,
  RefreshCw,
  StopCircle,
  Copy,
  Check,
  AlertTriangle,
  Flame,
  Terminal,
  Activity,
  Gauge,
  Code,
  BookOpen,
  Send,
  WifiOff,
  Cpu
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const PROMPT_PRESETS = [
  {
    id: "sanskrit_phonetics",
    title: "Vedic Sanskrit Phonetics & Svarita Rules",
    category: "Vedic Linguistics",
    prompt:
      "Explain the exact phonetic rules for Udatta (high), Anudatta (low), and Svarita (circumflex) pitch accents in Vedic Sanskrit chanting. Provide Devanagari examples, IAST transliteration, and exact Hz frequency shifts for vocal calibration.",
  },
  {
    id: "cf_workers_ai",
    title: "Cloudflare Workers AI SSE Handler (TypeScript)",
    category: "Edge Computing",
    prompt:
      "Write a production-ready Cloudflare Workers AI Hono endpoint in TypeScript that consumes @cf/meta/llama-3.1-8b-instruct with stream: true and streams Server-Sent Events (SSE) back to the client using ReadableStream.",
  },
  {
    id: "hawan_script",
    title: "Hawan Agnihotra Sacred Code Script",
    category: "Karmakanda Rituals",
    prompt:
      "Generate the procedural ritual algorithm for conducting a Hawan Agnihotra ceremony, including samagri proportions, mantra sequencing, and Samidha placement timing in JSON format with commentary.",
  },
  {
    id: "astro_hora",
    title: "Astro-Hora Matrix & Muhurta Calculation",
    category: "Jyotish Calculation",
    prompt:
      "Demonstrate the mathematical formula for calculating Rahu Kalam and Planetary Horas for any geographic coordinate (latitude/longitude) and sun position. Include Python/TypeScript implementation.",
  },
];

export default function AIStreamDemo() {
  const [selectedPreset, setSelectedPreset] = useState<string>(PROMPT_PRESETS[0].id);
  const [customPrompt, setCustomPrompt] = useState<string>(PROMPT_PRESETS[0].prompt);
  const [copied, setCopied] = useState<boolean>(false);
  const [simulateDrop, setSimulateDrop] = useState<boolean>(false);

  const {
    completion,
    isLoading,
    isStreaming,
    error,
    reconnectCount,
    isAborted,
    isDegraded,
    metrics,
    startStream,
    abortStream,
    reset,
    fallbackSyncFetch,
  } = useAIStream({
    endpoint: "/api/chat/stream",
    fallbackEndpoint: "/api/chat",
    maxRetries: 3,
    retryDelayMs: 800,
    autoReconnect: true,
    onChunk: (_chunk, _accumulated) => {
      // Optional logging or side effects on each chunk
    },
    onComplete: (_finalText) => {
      console.log("[AIStreamDemo] Stream finished cleanly!");
    },
    onError: (err, partialText) => {
      console.error("[AIStreamDemo] Stream error encountered:", err, "Partial len:", partialText.length);
    },
    onReconnect: (attempt, maxRetries) => {
      console.warn(`[AIStreamDemo] Reconnecting SSE stream... (${attempt}/${maxRetries})`);
    },
  });

  const handleSelectPreset = (presetId: string) => {
    const preset = PROMPT_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      setSelectedPreset(preset.id);
      setCustomPrompt(preset.prompt);
      reset();
    }
  };

  const handleStartStream = async () => {
    try {
      await startStream(customPrompt, {
        body: {
          simulateDrop, // Passes drop flag if simulation enabled
        },
      });
    } catch (err) {
      // Errors handled gracefully via hook state and ErrorBoundary
    }
  };

  const handleCopy = () => {
    if (completion) {
      navigator.clipboard.writeText(completion);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-neutral-900 via-amber-950/40 to-neutral-900 border border-amber-500/30 rounded-2xl p-6 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-amber-500/20 border border-amber-500/40 text-amber-300 font-mono text-xs rounded-full flex items-center gap-1.5 font-semibold">
                <Zap className="w-3.5 h-3.5 text-amber-400" /> Cloudflare Workers AI SSE
              </span>
              <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-mono text-xs rounded-full font-semibold">
                ReadableStreamDefaultReader
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-100 flex items-center gap-3 font-serif">
              <Sparkles className="w-7 h-7 text-amber-400 animate-pulse" />
              useAIStream Edge Orchestrator
            </h2>
            <p className="text-sm text-neutral-400 max-w-2xl">
              Real-time Server-Sent Events (SSE) streaming hook with auto-reconnection, markdown chunk accumulation, mid-stream abort resilience, and zero-drop state degradation.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => reset()}
              className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-xs font-semibold rounded-xl border border-neutral-700 transition-all cursor-pointer flex items-center gap-2 shadow-sm active:scale-95"
            >
              <RefreshCw className="w-4 h-4 text-neutral-400" /> Reset Hook State
            </button>
          </div>
        </div>
      </div>

      {/* Preset Prompts Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {PROMPT_PRESETS.map((p) => {
          const isSelected = selectedPreset === p.id;
          return (
            <button
              key={p.id}
              onClick={() => handleSelectPreset(p.id)}
              className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-3 ${
                isSelected
                  ? "bg-amber-500/10 border-amber-500/60 shadow-lg shadow-amber-500/5 ring-1 ring-amber-500/30"
                  : "bg-neutral-900/60 border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900"
              }`}
            >
              <div>
                <span className="text-[10px] uppercase font-mono tracking-wider text-amber-400/90 font-semibold block mb-1">
                  {p.category}
                </span>
                <h4 className="text-sm font-semibold text-neutral-100 line-clamp-2">
                  {p.title}
                </h4>
              </div>
              <div className="flex items-center justify-between text-xs text-neutral-400 pt-2 border-t border-neutral-800/80">
                <span className="flex items-center gap-1 font-mono text-[11px]">
                  <Code className="w-3.5 h-3.5 text-amber-400" /> Preset
                </span>
                {isSelected && <span className="text-amber-400 font-bold">Active</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Prompt Input & Stream Controls */}
      <div className="bg-neutral-900/80 border border-neutral-800 rounded-2xl p-5 space-y-4 shadow-xl">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400 font-mono flex items-center gap-2">
            <Terminal className="w-4 h-4 text-amber-400" /> Prompt Payload
          </label>
          <div className="flex items-center gap-2 text-xs">
            <label className="flex items-center gap-2 cursor-pointer text-neutral-400 hover:text-neutral-200 select-none">
              <input
                type="checkbox"
                checked={simulateDrop}
                onChange={(e) => setSimulateDrop(e.target.checked)}
                className="rounded border-neutral-700 bg-neutral-800 text-amber-500 focus:ring-amber-500/40"
              />
              <span className="flex items-center gap-1 font-mono text-xs">
                <WifiOff className="w-3.5 h-3.5 text-rose-400" /> Test Auto-Reconnection
              </span>
            </label>
          </div>
        </div>

        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder="Enter prompt for streaming generation..."
          rows={3}
          className="w-full bg-black/50 border border-neutral-800 rounded-xl p-3 text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all font-sans resize-y"
        />

        {/* Action Controls Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleStartStream}
              disabled={isLoading || !customPrompt.trim()}
              className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-neutral-950 font-bold text-xs rounded-xl shadow-lg shadow-amber-500/20 transition-all cursor-pointer flex items-center gap-2 active:scale-95"
            >
              {isStreaming ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-neutral-950" /> Streaming Active...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Start AI Stream
                </>
              )}
            </button>

            {isStreaming && (
              <button
                onClick={abortStream}
                className="px-4 py-2.5 bg-rose-950/60 hover:bg-rose-900/80 text-rose-200 border border-rose-800/60 font-semibold text-xs rounded-xl transition-all cursor-pointer flex items-center gap-2 active:scale-95 shadow-md"
              >
                <StopCircle className="w-4 h-4 text-rose-400" /> Abort Stream
              </button>
            )}

            <button
              onClick={() => fallbackSyncFetch(customPrompt)}
              disabled={isLoading || !customPrompt.trim()}
              className="px-4 py-2.5 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-50 text-neutral-300 font-semibold text-xs rounded-xl border border-neutral-700 transition-all cursor-pointer flex items-center gap-2"
            >
              <Cpu className="w-4 h-4 text-indigo-400" /> Fallback Sync
            </button>
          </div>

          {/* Status Indicator Badge */}
          <div className="flex items-center gap-2">
            {isStreaming && (
              <span className="px-3 py-1.5 bg-emerald-950/80 border border-emerald-500/50 text-emerald-400 font-mono text-xs rounded-full flex items-center gap-2 shadow-md animate-pulse">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                LIVE STREAMING
              </span>
            )}

            {reconnectCount > 0 && isStreaming && (
              <span className="px-3 py-1.5 bg-amber-950/80 border border-amber-500/50 text-amber-300 font-mono text-xs rounded-full flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-400" />
                RECONNECTING ({reconnectCount}/3)
              </span>
            )}

            {!isStreaming && completion && !isAborted && !isDegraded && (
              <span className="px-3 py-1.5 bg-indigo-950/80 border border-indigo-500/50 text-indigo-300 font-mono text-xs rounded-full flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-indigo-400" /> STREAM COMPLETE
              </span>
            )}

            {isAborted && (
              <span className="px-3 py-1.5 bg-rose-950/80 border border-rose-500/50 text-rose-300 font-mono text-xs rounded-full flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-400" /> ABORTED (Partial Preserved)
              </span>
            )}

            {isDegraded && (
              <span className="px-3 py-1.5 bg-violet-950/80 border border-violet-500/50 text-violet-300 font-mono text-xs rounded-full flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5 text-violet-400" /> DEGRADED TO SYNC
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Real-time Performance Metrics HUD */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-3 flex flex-col justify-center">
          <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5 text-amber-400" /> TTFB Latency
          </span>
          <span className="text-base font-bold text-neutral-100 font-mono mt-1">
            {metrics.ttfbMs !== null ? `${metrics.ttfbMs} ms` : "--"}
          </span>
        </div>

        <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-3 flex flex-col justify-center">
          <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 flex items-center gap-1">
            <Activity className="w-3.5 h-3.5 text-emerald-400" /> Total Chunks
          </span>
          <span className="text-base font-bold text-neutral-100 font-mono mt-1">
            {metrics.totalChunks}
          </span>
        </div>

        <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-3 flex flex-col justify-center">
          <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 flex items-center gap-1">
            <Code className="w-3.5 h-3.5 text-indigo-400" /> Total Chars
          </span>
          <span className="text-base font-bold text-neutral-100 font-mono mt-1">
            {metrics.totalChars}
          </span>
        </div>

        <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-3 flex flex-col justify-center">
          <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 flex items-center gap-1">
            <Zap className="w-3.5 h-3.5 text-amber-400" /> Speed (Char/s)
          </span>
          <span className="text-base font-bold text-neutral-100 font-mono mt-1">
            {metrics.charsPerSecond}
          </span>
        </div>

        <div className="col-span-2 sm:col-span-1 bg-neutral-900/60 border border-neutral-800 rounded-xl p-3 flex flex-col justify-center">
          <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5 text-rose-400" /> Reconnects
          </span>
          <span className="text-base font-bold text-neutral-100 font-mono mt-1">
            {reconnectCount} / 3
          </span>
        </div>
      </div>

      {/* Error Boundary Wrapped Streaming Output Container */}
      <AIStreamErrorBoundary fallbackText={completion} onResetStream={reset}>
        <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-6 shadow-2xl relative min-h-[280px]">
          <div className="flex items-center justify-between pb-4 border-b border-neutral-800/80 mb-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-rose-500/80" />
              <span className="w-3 h-3 rounded-full bg-amber-500/80" />
              <span className="w-3 h-3 rounded-full bg-emerald-500/80" />
              <span className="text-xs font-mono text-neutral-400 ml-2">
                stream_buffer_output.md
              </span>
            </div>

            {completion && (
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-mono text-xs rounded-lg transition-all cursor-pointer"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy Markdown"}
              </button>
            )}
          </div>

          {/* Stream Output Text Area */}
          <div className="prose prose-invert max-w-none text-neutral-200 text-sm font-mono leading-relaxed whitespace-pre-wrap overflow-x-auto min-h-[180px]">
            {completion ? (
              <>
                {completion}
                {isStreaming && (
                  <span className="inline-block w-2 h-4 ml-1 bg-amber-400 animate-pulse align-middle" />
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-neutral-500 space-y-3 font-sans">
                <Sparkles className="w-8 h-8 text-neutral-600 animate-pulse" />
                <p className="text-xs font-medium">
                  Select a prompt preset above and click "Start AI Stream" to launch the SSE ReadableStream.
                </p>
              </div>
            )}
          </div>

          {/* Simulated Error Banner */}
          {error && (
            <div className="mt-4 p-4 bg-rose-950/40 border border-rose-800/60 rounded-xl text-rose-200 text-xs flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                <span>{error.message}</span>
              </div>
              <button
                onClick={() => handleStartStream()}
                className="px-3 py-1 bg-rose-800 hover:bg-rose-700 text-white font-semibold rounded-lg shrink-0 cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </AIStreamErrorBoundary>
    </div>
  );
}
